/**
 * §31.2 (V131) — Vista de cocina dedicada (pantalla fija de cocina).
 *  - Lista las comandas en FIFO por hora de envío (server ya ordena).
 *  - Resalta lo que está por quemarse (verde→ámbar→rojo vs umbral del tenant).
 *  - Marca un platillo listo con UN TOQUE, sin diálogos.
 *  - Nada del flujo de cobro.
 * Riesgo A (audio bloqueado hasta interacción): botón "Activar sonido" que arma el
 *   AudioContext con el gesto del usuario. La marca visual es el respaldo independiente
 *   → la vista es útil en silencio.
 * Riesgo B (sesión pasiva): el aviso global de expiración (5 min antes) protege esta
 *   pantalla; además el tenant puede subir su ventana de sesión a 12h (Configuración).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Volume2, VolumeX, Bell, Check, Flame, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import {
  ageSeconds, fmtElapsed, kitchenColor, KITCHEN_CARD, KITCHEN_DOT, KITCHEN_TEXT,
  clampKitchenAlertMinutes,
} from '../lib/kitchen';

interface KItem {
  id: string; description: string; qty: number; notes: string | null;
  firedAt: string | null; readyAt: string | null;
  modifiers: { name: string; groupName: string | null }[];
}
interface KOrder {
  orderId: string; tableLabel: string; branchId: string;
  nudgedAt: string | null; oldestPendingFiredAt: string; items: KItem[];
  /** §32/A0 — pedido para recoger (mostrador/público): el feed lo distingue con badge. */
  pickup?: boolean; deliveryCode?: string | null;
}

const POLL_MS = 7000; // G5 — polling 5–10s (nada de websockets en v1)

export function KitchenView() {
  const { user } = useAuth();
  const threshold = clampKitchenAlertMinutes(user?.tenant?.kitchenAlertMinutes);
  // V169 (🅑) — dos estaciones: pestañas Cocina/Barra. Con una línea NO se muestran
  // (el tenant no ve dos vistas). Lee de /me → cambia en vivo con el toggle, sin recargar.
  const twoStations = !!user?.tenant?.kitchenTwoStations;
  const [station, setStation] = useState<'cocina' | 'barra'>('cocina');
  const [orders, setOrders] = useState<KOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [soundOn, setSoundOn] = useState(false);
  const [busyItem, setBusyItem] = useState<string | null>(null);

  // Audio armado con gesto del usuario (riesgo A). El beep es enhancement.
  const audioRef = useRef<AudioContext | null>(null);
  const prevRedIds = useRef<Set<string>>(new Set());
  const prevNudge = useRef<Map<string, string>>(new Map());

  const beep = useCallback(() => {
    const ctx = audioRef.current;
    if (!ctx) return;
    try {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine'; o.frequency.value = 880; g.gain.value = 0.08;
      o.start(); o.stop(ctx.currentTime + 0.25);
    } catch { /* audio no disponible */ }
  }, []);

  const armSound = () => {
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!audioRef.current) audioRef.current = new Ctx();
      void audioRef.current.resume();
      setSoundOn(true);
      beep(); // confirmación audible del gesto
    } catch { setSoundOn(false); }
  };

  const load = useCallback(async () => {
    try {
      // V169 (🅑) — con dos estaciones, la vista pide solo su estación; con una, todo.
      const path = twoStations ? `/pos/kitchen?station=${station}` : '/pos/kitchen';
      const data = (await api(path)) as KOrder[];
      setErr(null);
      setOrders(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      setErr((e as { body?: { message?: string }; message?: string })?.body?.message
        || (e as { message?: string })?.message || 'No se pudo cargar cocina');
    } finally { setLoading(false); }
  }, [twoStations, station]);

  // Polling de datos (G5) + tick local de 1s (avanza cronómetros/colores sin refetch).
  useEffect(() => {
    load();
    const poll = window.setInterval(load, POLL_MS);
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => { window.clearInterval(poll); window.clearInterval(tick); };
  }, [load]);

  // Señal audible: una comanda NUEVA en rojo o un "presionar a cocina" nuevo. Solo si
  // el sonido está armado; la marca visual siempre está (respaldo independiente).
  useEffect(() => {
    const red = new Set<string>();
    let ring = false;
    for (const o of orders) {
      const c = kitchenColor(ageSeconds(o.oldestPendingFiredAt, now), threshold);
      if (c === 'red') {
        red.add(o.orderId);
        if (!prevRedIds.current.has(o.orderId)) ring = true;
      }
      if (o.nudgedAt && prevNudge.current.get(o.orderId) !== o.nudgedAt) ring = true;
      if (o.nudgedAt) prevNudge.current.set(o.orderId, o.nudgedAt);
    }
    prevRedIds.current = red;
    if (ring && soundOn) beep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, now]);

  const markReady = async (o: KOrder, it: KItem) => {
    setBusyItem(it.id);
    try {
      await api(`/pos/kitchen/orders/${o.orderId}/items/${it.id}/ready`, { method: 'POST', body: { ready: true } });
      await load();
    } catch { /* re-poll lo corrige */ } finally { setBusyItem(null); }
  };

  const nudgeRecent = (iso: string | null) => !!iso && (now - new Date(iso).getTime()) < 3 * 60 * 1000;

  const summary = useMemo(() => {
    let red = 0, amber = 0;
    for (const o of orders) {
      const c = kitchenColor(ageSeconds(o.oldestPendingFiredAt, now), threshold);
      if (c === 'red') red++; else if (c === 'amber') amber++;
    }
    return { red, amber, total: orders.length };
  }, [orders, now, threshold]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl tracking-tight text-slate-900">{twoStations && station === 'barra' ? 'Barra' : 'Cocina'}</h1>
          <p className="text-sm text-slate-500">
            Comandas en preparación (FIFO). Umbral del semáforo: <b>{threshold} min</b>.
            {summary.total > 0 && <> · <span className="text-rose-700">{summary.red} por quemarse</span>, <span className="text-amber-700">{summary.amber} cerca</span>.</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
            <RefreshCw className="h-4 w-4" /> Actualizar
          </button>
          {soundOn ? (
            <span className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
              <Volume2 className="h-4 w-4" /> Sonido activo
            </span>
          ) : (
            <button onClick={armSound} className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800">
              <VolumeX className="h-4 w-4" /> Activar sonido
            </button>
          )}
        </div>
      </div>

      {/* V169 (🅑) — pestañas Cocina/Barra (solo con dos estaciones). Cambiar de pestaña
          repide el feed de esa estación (load depende de `station`). */}
      {twoStations && (
        <div className="mb-4 inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
          {(['cocina', 'barra'] as const).map((s) => (
            <button key={s} onClick={() => setStation(s)}
              className={`rounded-lg px-4 py-1.5 text-sm font-medium capitalize transition ${station === s ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {s}
            </button>
          ))}
        </div>
      )}

      {!soundOn && (
        <p className="mb-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
          El navegador bloquea el sonido hasta activarlo. Toca <b>Activar sonido</b> para escuchar avisos; la señal visual (colores) funciona igual sin sonido.
        </p>
      )}

      {err && <p className="mb-3 rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</p>}

      {loading ? (
        <p className="text-sm text-slate-400">Cargando…</p>
      ) : orders.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
          <Flame className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">No hay comandas en preparación.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {orders.map((o) => {
            const age = ageSeconds(o.oldestPendingFiredAt, now);
            const color = kitchenColor(age, threshold);
            const nudged = nudgeRecent(o.nudgedAt);
            return (
              <div key={o.orderId} className={`rounded-2xl border-2 p-3 ${KITCHEN_CARD[color]}`}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`h-3 w-3 rounded-full ${KITCHEN_DOT[color]}`} />
                    <span className="text-sm font-semibold text-slate-800">{o.tableLabel}</span>
                    {/* §32/A0 — badge para pedidos de mostrador/recoger. */}
                    {o.pickup && (
                      <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold uppercase text-indigo-700">
                        Recoger{o.deliveryCode ? ` #${o.deliveryCode}` : ''}
                      </span>
                    )}
                    {nudged && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                        <Bell className="h-3 w-3" /> Apúrense
                      </span>
                    )}
                  </div>
                  <span className={`tabular-nums text-sm font-bold ${KITCHEN_TEXT[color]}`}>{fmtElapsed(age)}</span>
                </div>
                <ul className="space-y-1.5">
                  {o.items.map((it) => {
                    const ready = !!it.readyAt;
                    return (
                      <li key={it.id} className="flex items-start justify-between gap-2 rounded-xl bg-white/70 px-2.5 py-1.5">
                        <div className={ready ? 'text-slate-400 line-through' : 'text-slate-800'}>
                          <span className="text-sm font-medium">{it.qty}× {it.description}</span>
                          {it.modifiers.length > 0 && (
                            <span className="block text-[11px] text-slate-500">
                              {it.modifiers.map((m) => m.name).join(' · ')}
                            </span>
                          )}
                          {it.notes && <span className="block text-[11px] italic text-slate-500">“{it.notes}”</span>}
                        </div>
                        {!ready && (
                          <button
                            onClick={() => markReady(o, it)}
                            disabled={busyItem === it.id}
                            title="Marcar listo"
                            className="shrink-0 rounded-lg bg-emerald-600 p-1.5 text-white hover:bg-emerald-700 disabled:opacity-50">
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-6 text-center text-[11px] text-slate-400">
        ¿La pantalla se cierra sola? Un admin puede subir la <b>ventana de sesión</b> (hasta 12h) en Configuración › Ventana de sesión para el equipo de cocina.
      </p>
    </div>
  );
}
